import {
  SUPPORT_DIAGNOSTIC_SCHEMA,
  type SupportDiagnosticBufferStats,
  type SupportDiagnosticEvent,
  type SupportDiagnosticEventInput,
  type SupportDiagnosticSnapshot,
  type SupportDiagnosticValue,
} from '@/types/supportDiagnostic';
import naclUtil from 'tweetnacl-util';
import { APP_CONSTANTS } from '@/config/constants';

export const SUPPORT_DIAGNOSTIC_MAX_EVENTS = APP_CONSTANTS.DEBUG.LOG_BUFFER.MAX_EVENTS;
export const SUPPORT_DIAGNOSTIC_MAX_BYTES = APP_CONSTANTS.DEBUG.LOG_BUFFER.MAX_PACKAGE_BYTES;

const DEFAULT_BURST_WINDOW_MS = 2_000;
const MAX_DEPTH = 32;
const REDACTED = '[redacted]';

const SENSITIVE_KEY_PART = /(?:^|_)(?:authorization|cookie|password|passwd|senha|token|jwt|secret|service_role|api_?keys?|apikeys?|private_keys?|public_keys?|anon_keys?|publishable_keys?)(?:_|$)/i;

const JWT = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g;
const BEARER = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]{8,}/gi;
const SECRET_ASSIGNMENT = /\b(authorization|cookie|password|senha|token|api[_-]?key|secret)\s*[:=]\s*[^\s,;&]+/gi;
const SUPABASE_KEY = /\bsb_(?:secret|publishable)_[A-Za-z0-9_-]+\b/g;
const JSON_STRING_FIELD = /"([^"\\]+)"\s*:\s*"(?:\\.|[^"\\])*"/g;

const utf8Bytes = (value: string): number => naclUtil.decodeUTF8(value).length;

const normalizeKey = (key: string): string => key
  .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
  .replace(/[\s.-]+/g, '_')
  .toLowerCase();

export const sanitizeSupportDiagnosticString = (raw: string): string => {
  return raw
    .replace(JSON_STRING_FIELD, (match, key: string) => (
      SENSITIVE_KEY_PART.test(normalizeKey(key)) ? `"${key}":"${REDACTED}"` : match
    ))
    .replace(JWT, '[jwt-redacted]')
    .replace(BEARER, '$1 [credential-redacted]')
    .replace(SECRET_ASSIGNMENT, (_match, label: string) => `${label}=[redacted]`)
    .replace(SUPABASE_KEY, '[supabase-key-redacted]');
};

const sanitizeValue = (
  input: unknown,
  depth: number,
  ancestors: Set<object>
): SupportDiagnosticValue => {
  if (input === null || typeof input === 'boolean') return input;
  if (typeof input === 'number') return Number.isFinite(input) ? input : String(input);
  if (typeof input === 'string') return sanitizeSupportDiagnosticString(input);
  if (typeof input === 'undefined') return '[undefined]';
  if (typeof input === 'bigint') return input.toString();
  if (typeof input === 'symbol' || typeof input === 'function') return `[${typeof input}]`;
  if (depth >= MAX_DEPTH) return '[blocked:max-depth]';
  if (input instanceof Date) return input.toISOString();
  if (input instanceof Error) {
    return {
      name: sanitizeSupportDiagnosticString(input.name),
      message: sanitizeSupportDiagnosticString(input.message),
    };
  }
  if (ArrayBuffer.isView(input)) {
    return Array.from(new Uint8Array(input.buffer, input.byteOffset, input.byteLength));
  }
  if (input instanceof ArrayBuffer) {
    return Array.from(new Uint8Array(input));
  }
  if (typeof input !== 'object') return sanitizeSupportDiagnosticString(String(input));
  if (ancestors.has(input)) return '[blocked:circular-reference]';

  const nextAncestors = new Set(ancestors);
  nextAncestors.add(input);

  if (Array.isArray(input)) {
    return input.map(item => sanitizeValue(item, depth + 1, nextAncestors));
  }

  const output: Record<string, SupportDiagnosticValue> = {};
  for (const [rawKey, rawValue] of Object.entries(input as Record<string, unknown>)) {
    const key = sanitizeSupportDiagnosticString(rawKey);
    const normalizedKey = normalizeKey(rawKey);
    if (SENSITIVE_KEY_PART.test(normalizedKey)) {
      output[key] = REDACTED;
    } else {
      output[key] = sanitizeValue(rawValue, depth + 1, nextAncestors);
    }
  }
  return output;
};

export const sanitizeSupportDiagnosticValue = (input: unknown): SupportDiagnosticValue => (
  (() => {
    try {
      return sanitizeValue(input, 0, new Set());
    } catch {
      return '[blocked:sanitization-error]';
    }
  })()
);

const canonicalize = (value: SupportDiagnosticValue): SupportDiagnosticValue => {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce<Record<string, SupportDiagnosticValue>>((result, key) => {
        result[key] = canonicalize(value[key]);
        return result;
      }, {});
  }
  return value;
};

const canonicalStringify = (value: SupportDiagnosticValue): string => (
  JSON.stringify(canonicalize(value))
);

const deepFreeze = <T>(value: T): T => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value as Record<string, unknown>).forEach(deepFreeze);
  }
  return value;
};

interface SupportDiagnosticBufferOptions {
  maxEvents?: number;
  maxBytes?: number;
  burstWindowMs?: number;
  previousStats?: Pick<SupportDiagnosticBufferStats,
    'droppedByEventLimit' | 'droppedByByteLimit' | 'compactedEventCount'>;
  now?: () => Date;
}

export class SupportDiagnosticBuffer {
  private readonly maxEvents: number;
  private readonly maxBytes: number;
  private readonly burstWindowMs: number;
  private readonly now: () => Date;
  private events: SupportDiagnosticEvent[] = [];
  private eventBytes: number[] = [];
  private totalBytes = 0;
  private nextId = 1;
  private droppedByEventLimit = 0;
  private droppedByByteLimit = 0;
  private compactedEventCount = 0;

  constructor(options: SupportDiagnosticBufferOptions = {}) {
    this.maxEvents = Math.min(
      SUPPORT_DIAGNOSTIC_MAX_EVENTS,
      Math.max(1, Math.floor(options.maxEvents ?? SUPPORT_DIAGNOSTIC_MAX_EVENTS))
    );
    this.maxBytes = Math.min(
      SUPPORT_DIAGNOSTIC_MAX_BYTES,
      Math.max(1, Math.floor(options.maxBytes ?? SUPPORT_DIAGNOSTIC_MAX_BYTES))
    );
    this.burstWindowMs = Math.max(0, options.burstWindowMs ?? DEFAULT_BURST_WINDOW_MS);
    this.now = options.now ?? (() => new Date());
    // Repreparar uma captura não apaga perdas anteriores; bytes e quantidade são recalculados.
    const count = (value: number | undefined): number => (
      typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : 0
    );
    this.droppedByEventLimit = count(options.previousStats?.droppedByEventLimit);
    this.droppedByByteLimit = count(options.previousStats?.droppedByByteLimit);
    this.compactedEventCount = count(options.previousStats?.compactedEventCount);
  }

  record(input: SupportDiagnosticEventInput): void {
    const occurredAt = input.occurredAt ?? this.now().toISOString();
    const sanitized: SupportDiagnosticEvent = {
      id: this.nextId++,
      occurredAt,
      lastOccurredAt: occurredAt,
      level: input.level,
      category: sanitizeSupportDiagnosticString(input.category),
      name: sanitizeSupportDiagnosticString(input.name),
      message: sanitizeSupportDiagnosticString(input.message ?? ''),
      data: sanitizeSupportDiagnosticValue(input.data ?? null),
      repetitions: 1,
    };

    const previous = this.events[this.events.length - 1];
    if (previous && this.isSameBurst(previous, sanitized)) {
      const compacted = {
        ...previous,
        lastOccurredAt: occurredAt,
        repetitions: previous.repetitions + 1,
      };
      const index = this.events.length - 1;
      this.totalBytes -= this.eventBytes[index];
      this.events[index] = compacted;
      this.eventBytes[index] = this.measureEvent(compacted);
      this.totalBytes += this.eventBytes[index];
      this.compactedEventCount += 1;
    } else {
      const byteLength = this.measureEvent(sanitized);
      this.events.push(sanitized);
      this.eventBytes.push(byteLength);
      this.totalBytes += byteLength;
    }

    this.enforceLimits();
  }

  createImmutableSnapshot(input: {
    diagnosticId: string;
    createdAt?: string;
    metadata?: unknown;
  }): SupportDiagnosticSnapshot {
    const snapshot: SupportDiagnosticSnapshot = {
      schema: SUPPORT_DIAGNOSTIC_SCHEMA,
      diagnosticId: input.diagnosticId,
      createdAt: input.createdAt ?? this.now().toISOString(),
      metadata: sanitizeSupportDiagnosticValue(input.metadata ?? null),
      events: this.events.map(event => ({ ...event,
        category: sanitizeSupportDiagnosticString(event.category),
        name: sanitizeSupportDiagnosticString(event.name),
        message: sanitizeSupportDiagnosticString(event.message),
        data: sanitizeSupportDiagnosticValue(event.data),
      })),
      stats: this.getStats(),
    };
    return deepFreeze(snapshot);
  }

  getStats(): SupportDiagnosticBufferStats {
    return {
      eventCount: this.events.length,
      byteLength: this.totalBytes,
      droppedByEventLimit: this.droppedByEventLimit,
      droppedByByteLimit: this.droppedByByteLimit,
      compactedEventCount: this.compactedEventCount,
    };
  }

  clear(): void {
    this.events = [];
    this.eventBytes = [];
    this.totalBytes = 0;
  }

  private measureEvent(event: SupportDiagnosticEvent): number {
    return utf8Bytes(JSON.stringify(event));
  }

  private isSameBurst(previous: SupportDiagnosticEvent, next: SupportDiagnosticEvent): boolean {
    const elapsed = Date.parse(next.occurredAt) - Date.parse(previous.lastOccurredAt);
    return elapsed >= 0 && elapsed <= this.burstWindowMs &&
      previous.level === next.level &&
      previous.category === next.category &&
      previous.name === next.name &&
      previous.message === next.message &&
      canonicalStringify(previous.data) === canonicalStringify(next.data);
  }

  private enforceLimits(): void {
    while (this.events.length > this.maxEvents) {
      this.removeOldest();
      this.droppedByEventLimit += 1;
    }
    while (this.totalBytes > this.maxBytes && this.events.length > 0) {
      this.removeOldest();
      this.droppedByByteLimit += 1;
    }
  }

  private removeOldest(): void {
    this.events.shift();
    const removedBytes = this.eventBytes.shift() ?? 0;
    this.totalBytes = Math.max(0, this.totalBytes - removedBytes);
  }
}
